import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { toast } from 'react-hot-toast';

export default function AutoPOCompleter() {
  const queryClient = useQueryClient();
  const [hasChecked, setHasChecked] = useState(false);

  const { data: pos = [] } = useQuery({ 
    queryKey: ['production-orders'], 
    queryFn: () => base44.entities.ProductionOrder.list(),
    enabled: !hasChecked
  });

  const { data: tasks = [] } = useQuery({ 
    queryKey: ['production-tasks'], 
    queryFn: () => base44.entities.ProductionTask.list(),
    enabled: !hasChecked
  });

  const { data: barangKeluar = [] } = useQuery({ 
    queryKey: ['barang-keluar'], 
    queryFn: () => base44.entities.BarangKeluar.list(),
    enabled: !hasChecked
  });

  useEffect(() => {
    if (hasChecked || pos.length === 0 || tasks.length === 0) return;
    
    let processedAny = false;

    const checkAndComplete = async () => {
      const shippingPOs = pos.filter(po => po.status === 'shipping' && po.is_shipped);
      const now = new Date();

      for (const order of shippingPOs) {
        let latestShipmentDate = null;
        if (order.shipments && order.shipments.length > 0) {
            latestShipmentDate = new Date(order.shipped_at || order.updated_date);
        } else {
            latestShipmentDate = new Date(order.shipped_at || order.updated_date);
        }

        const diffTime = now.getTime() - latestShipmentDate.getTime();
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        if (diffDays >= 3) {
            const totalTargetDisplay = order?.products?.reduce((sum, p) => sum + (Number(p.qty) || Number(p.target_qty) || 0), 0) || Number(order?.target_qty) || 0;
            
            let totalRejectsDisplay = 0;
            tasks.forEach(task => {
                if (task.po_id === order.id && task.member_progress) {
                    Object.values(task.member_progress).forEach(prog => {
                        if (prog.status === 'completed' && prog.rejected_items) {
                            prog.rejected_items.forEach(rj => {
                                rj.items.forEach(item => {
                                    if (item.qty > 0) totalRejectsDisplay += Number(item.qty);
                                });
                            });
                        }
                    });
                }
            });

            const totalShippedDisplay = order?.shipments?.reduce((sum, s) => sum + (s.items || []).reduce((s2, i) => s2 + (Number(i.qty_akhir) || 0), 0), 0) || 0;
            const isFullyShipped = totalShippedDisplay >= Math.max(0, totalTargetDisplay - totalRejectsDisplay);

            if (isFullyShipped) {
                console.log(`[AutoPOCompleter] Auto completing PO ${order.order_number || order.id} (shipped >= 3 days ago)`);
                try {
                    await base44.entities.ProductionOrder.update(order.id, { 
                      status: 'done',
                      is_synced_to_delivery: true 
                    });
                    
                    if (order.order_number) {
                        await base44.entities.ClientPO.update(order.order_number, { statusProduksi: 'SUDAH SAMPAI' });
                    }
                    processedAny = true;
                } catch (e) {
                    console.error('[AutoPOCompleter] Failed to auto complete PO', e);
                }
            }
        }
      }

      if (processedAny) {
          queryClient.invalidateQueries({ queryKey: ['production-orders'] });
          queryClient.invalidateQueries({ queryKey: ['barang-keluar'] });
          queryClient.invalidateQueries({ queryKey: ['client-pos'] });
          toast.success('Sistem secara otomatis menyelesaikan PO yang sudah terkirim lebih dari 3 hari lalu.');
      }
      
      setHasChecked(true);
    };

    checkAndComplete();

  }, [pos, tasks, hasChecked, queryClient]);

  return null;
}
